// # Step 1 Library Component
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthStore } from "../../../auth/state";
import "./library_step_1.css";
import "../lib_components/lib_animation.css"; // # Import animations
import DashboardFooter from "../../../components/DashboardFooter";
import LibraryHeader from "../lib_components/lib_header";
import WarningFlood from "../lib_components/warning_flood"; // # Import WarningFlood component
import { doc, updateDoc, onSnapshot } from "firebase/firestore"; // # Import for updating theme
import { db } from "../../../auth/firebase"; // # Import db
import cacheManager from "../../../auth/cache_manager"; // # Import cache manager

// # Import icons
import anatomyIcon from "./step1_icons/anatomy.png";
import biochemistryIcon from "./step1_icons/biochemistry.png";
import bloodIcon from "./step1_icons/blood.png";
import cnsIcon from "./step1_icons/cns.png";
import endocrineIcon from "./step1_icons/endocrine.png";
import eyeIcon from "./step1_icons/eye.png";
import gitIcon from "./step1_icons/git.png";
import heartIcon from "./step1_icons/heart.png";
import immunologyIcon from "./step1_icons/immunology.png";
import infectiousIcon from "./step1_icons/infectious.png";
import kidneyIcon from "./step1_icons/kidney.png";
import lungsIcon from "./step1_icons/lungs.png";
import musculoskeletalIcon from "./step1_icons/musculoskeletal.png";
import pathologyIcon from "./step1_icons/pathology.png";
import pharmaIcon from "./step1_icons/pharma.png";
import psychiatryIcon from "./step1_icons/psychiatry.png";
import publicHealthIcon from "./step1_icons/public_health.png";
import reproductiveIcon from "./step1_icons/reproductive-system.png";
import revisionIcon from "./step1_icons/revision.png";
import skinIcon from "./step1_icons/skin.png";
import statisticsIcon from "./step1_icons/statistics.png";
import { FaChartLine, FaCheckCircle } from "react-icons/fa"; // # Import stats icon and checkmark icon

// # Import chapter topics for dynamic counting
import { chapterTopics } from "./step1_chapter.js";

// # Total enabled chapters
const TOTAL_ENABLED_CHAPTERS = 15;

// # Chapter data with icons (ids fixed to be unique and sequential)
export const chapters = [
  { id: 1, title: "Biochemistry", icon: biochemistryIcon, enabled: true },
  { id: 2, title: "Immunology", icon: immunologyIcon, enabled: true },
  { id: 3, title: "Microbiology", icon: infectiousIcon, enabled: true },
  { id: 4, title: "General Pathology", icon: pathologyIcon, enabled: true },
  { id: 5, title: "Pharmacology", icon: pharmaIcon, enabled: true },
  {
    id: 6,
    title: "Biostatistics & Epidemiology",
    icon: statisticsIcon,
    enabled: true,
  },
  {
    id: 7,
    title: "Quality & Healthcare Delivery",
    icon: publicHealthIcon,
    enabled: true,
  },
  { id: 8, title: "Anatomy & Histology", icon: anatomyIcon, enabled: false },
  { id: 9, title: "Cardiovascular", icon: heartIcon, enabled: true },
  { id: 10, title: "Endocrine", icon: endocrineIcon, enabled: true },
  { id: 11, title: "Gastrointestinal", icon: gitIcon, enabled: true },
  { id: 12, title: "Hematology & Oncology", icon: bloodIcon, enabled: true },
  {
    id: 13,
    title: "Musculoskeletal & Connective Tissue",
    icon: musculoskeletalIcon,
    enabled: true,
  },
  { id: 14, title: "Dermatology", icon: skinIcon, enabled: true },
  { id: 15, title: "Neurology", icon: cnsIcon, enabled: true },
  { id: 16, title: "Special Senses", icon: eyeIcon, enabled: true },
  { id: 17, title: "Psychiatry", icon: psychiatryIcon, enabled: true },
  { id: 18, title: "Renal", icon: kidneyIcon, enabled: true },
  { id: 19, title: "Reproductive", icon: reproductiveIcon, enabled: true },
  { id: 20, title: "Respiratory", icon: lungsIcon, enabled: true },
  { id: 21, title: "Rapid Review", icon: revisionIcon, enabled: false },
  {
    id: 22,
    title: "Ethics And Communication Skills",
    icon: publicHealthIcon,
    enabled: true,
  },
];

// # Calculate total topics from enabled chapters
const TOTAL_HARDCODED_TOPICS = chapters.reduce((total, chapter) => {
  if (chapter.enabled) {
    // # Special handling for chapters with naming inconsistencies
    let chapterSlug;
    if (chapter.title === "Hematology & Oncology") {
      chapterSlug = "hematology_and_oncology";
    } else if (chapter.title === "Musculoskeletal & Connective Tissue") {
      chapterSlug = "musculoskeletal_and_connective_tissue";
    } else if (chapter.title === "Ethics & Communication") {
      chapterSlug = "ethics_and_communication";
    } else {
      chapterSlug = chapter.title.toLowerCase().replace(/[\s&]+/g, "_");
    }

    return total + (chapterTopics[chapterSlug]?.length || 0);
  }
  return total;
}, 0);

function LibraryStep1() {
  const { userProfile, user } = useAuthStore();
  const navigate = useNavigate();
  const [isDarkMode, setIsDarkMode] = useState(false); // # Add dark mode state
  const [libraryStats, setLibraryStats] = useState({
    completedTopics: 0,
    totalTopics: 0,
    completedChapters: 0,
    totalChapters: 0,
  });
  const [chapterData, setChapterData] = useState({}); // # Add state for chapter data

  // # Load theme preference from user data on initial render
  useEffect(() => {
    const loadThemePreference = async () => {
      if (user && user.uid) {
        // # Try to get from cache first
        const userData = cacheManager.get(`user_${user.uid}`);
        if (
          userData &&
          userData.themes &&
          userData.themes.lib_theme !== undefined
        ) {
          setIsDarkMode(userData.themes.lib_theme === "dark");
        }
      }
    };

    loadThemePreference();
  }, [user]);

  // # Load library progress from Firestore
  useEffect(() => {
    if (!user?.uid) return;

    const progressRef = doc(db, "my_lib_progress", user.uid);
    const unsubscribe = onSnapshot(progressRef, (docSnapshot) => {
      if (docSnapshot.exists()) {
        const data = docSnapshot.data();
        if (data.step1) {
          let completedTopics = 0;
          let totalTopics = 0;
          let completedChapters = 0;

          Object.keys(data.step1).forEach((chapter) => {
            const chapterData = data.step1[chapter];
            // # Get all topic keys (excluding special fields)
            const topicKeys = Object.keys(chapterData).filter(
              (key) =>
                key !== "iscomplete" &&
                key !== "iscomplete_at" &&
                key !== "IsChapStarted" &&
                key !== "ChptStartedAt",
            );

            // # Count completed topics
            const chapterCompletedTopics = topicKeys.filter(
              (topic) => chapterData[topic]?.completed,
            ).length;

            // # Get total topics for this chapter from chapterTopics mapping
            const chapterTotalTopics = chapterTopics[chapter]?.length || 0;

            completedTopics += chapterCompletedTopics;
            totalTopics += chapterTotalTopics;

            if (chapterData.iscomplete) {
              completedChapters++;
            }
          });

          setLibraryStats({
            completedTopics,
            totalTopics: TOTAL_HARDCODED_TOPICS,
            completedChapters,
            totalChapters: TOTAL_ENABLED_CHAPTERS,
          });

          // Store chapter completion data
          setChapterData(data.step1);
        }
      }
    });

    return () => unsubscribe();
  }, [user]);

  // # Handle theme toggle
  const handleThemeToggle = async () => {
    try {
      const newTheme = !isDarkMode;
      setIsDarkMode(newTheme);

      // # Update theme in Firebase if user is logged in
      if (user && user.uid) {
        // # Update the user document
        await updateDoc(doc(db, "users", user.uid), {
          "themes.lib_theme": newTheme ? "dark" : "light",
        });

        // # Update cache manager
        const userData = cacheManager.get(`user_${user.uid}`);
        if (userData) {
          const updatedUserData = {
            ...userData,
            themes: {
              ...(userData.themes || {}),
              lib_theme: newTheme ? "dark" : "light",
            },
          };
          cacheManager.set(`user_${user.uid}`, updatedUserData);
        }
      }

      // # Dispatch theme change event for footer and other components
      const themeChangeEvent = new CustomEvent("theme_change", {
        detail: { isDark: newTheme },
      });
      window.dispatchEvent(themeChangeEvent);
    } catch (error) {
      console.error("Error updating theme preference:", error);
    }
  };

  // # Basic authentication check - no subscription check needed
  useEffect(() => {
    if (!user?.uid) {
      navigate("/signin");
    }
  }, [user, navigate]);

  // # Handle chapter click
  const handleChapterClick = (chapter) => {
    // # Special handling for chapters with naming inconsistencies
    let chapterSlug;
    if (chapter.title === "Hematology & Oncology") {
      chapterSlug = "hematology_and_oncology";
    } else if (chapter.title === "Musculoskeletal & Connective Tissue") {
      chapterSlug = "musculoskeletal_and_connective_tissue";
    } else if (chapter.title === "Ethics & Communication") {
      chapterSlug = "ethics_and_communication";
    } else if (chapter.title === "Biostatistics & Epidemiology") {
      chapterSlug = "biostatistics_and_epidemiology";
    } else {
      chapterSlug = chapter.title.toLowerCase().replace(/[\s&]+/g, "_");
    }

    const isAdmin = window.location.pathname.startsWith("/admin");
    const basePath = isAdmin ? "/admin/library/step1" : "/library/step1";
    navigate(`${basePath}/${chapterSlug}`);
  };

  // # Handle stats click
  const handleStatsClick = () => {
    const isAdmin = window.location.pathname.startsWith("/admin");
    const basePath = isAdmin ? "/admin/library/step1" : "/library/step1";
    navigate(`${basePath}/stats`);
  };

  return (
    <div
      className={`step1_library-container ${
        isDarkMode ? "step1_dark-mode" : ""
      }`}
      style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/icons/dash/usmle_step1.jpg)` }}
    >
      <WarningFlood />
      <LibraryHeader
        isDarkMode={isDarkMode}
        onThemeToggle={handleThemeToggle}
      />

      {/* Main Content */}
      <div className="step1_library-main-content">
        <div className="step1_page-title">
          <h1>
            USMLE Step 1 Library -{" "}
            {userProfile?.firstName || user?.email?.split("@")[0]}
          </h1>
        </div>

        {/* Stats Card */}
        <div className="step1_stats-card">
          <div className="step1_stats-content">
            <div className="step1_stat-item">
              <span className="step1_stat-label">Topics Completed</span>
              <span className="step1_stat-value">
                {libraryStats.completedTopics}/{libraryStats.totalTopics}
              </span>
              <div className="step1_progress-bar">
                <div
                  className="step1_progress-fill"
                  style={{
                    width: `${
                      (libraryStats.completedTopics /
                        libraryStats.totalTopics) *
                      100
                    }%`,
                  }}
                />
              </div>
            </div>

            <div className="step1_stat-item">
              <span className="step1_stat-label">Chapters Completed</span>
              <span className="step1_stat-value">
                {libraryStats.completedChapters}/{libraryStats.totalChapters}
              </span>
              <div className="step1_progress-bar">
                <div
                  className="step1_progress-fill"
                  style={{
                    width: `${
                      (libraryStats.completedChapters /
                        libraryStats.totalChapters) *
                      100
                    }%`,
                  }}
                />
              </div>
            </div>

            <button className="step1_stats-button" onClick={handleStatsClick}>
              <FaChartLine />
              <span>View Stats</span>
            </button>
          </div>
        </div>

        <div className="step1_chapters-grid">
          {chapters
            .filter((chapter) => chapter.enabled)
            .map((chapter) => {
              // # Create chapter slug for matching with data
              let chapterSlug;
              if (chapter.title === "Hematology & Oncology") {
                chapterSlug = "hematology_and_oncology";
              } else if (
                chapter.title === "Musculoskeletal & Connective Tissue"
              ) {
                chapterSlug = "musculoskeletal_and_connective_tissue";
              } else {
                chapterSlug = chapter.title
                  .toLowerCase()
                  .replace(/[\s&]+/g, "_");
              }

              // # Calculate chapter progress
              const chapterProgress = (() => {
                if (!chapterData[chapterSlug]) return 0;

                // # If chapter is marked as complete, return 100% regardless of individual topics
                if (chapterData[chapterSlug].iscomplete) return 100;

                const topicKeys = Object.keys(chapterData[chapterSlug]).filter(
                  (key) => key !== "iscomplete" && key !== "iscomplete_at",
                );
                const completedTopics = topicKeys.filter(
                  (key) => chapterData[chapterSlug][key]?.completed,
                ).length;
                return topicKeys.length > 0
                  ? Math.round((completedTopics / topicKeys.length) * 100)
                  : 0;
              })();

              return (
                <div
                  key={chapter.id}
                  className="step1_chapter-tile"
                  onClick={() => handleChapterClick(chapter)}
                >
                  <div className="step1_chapter-icon">
                    {chapter.icon ? (
                      <img
                        src={chapter.icon}
                        alt={chapter.title}
                        className="step1_chapter-icon-image"
                      />
                    ) : (
                      <div className="step1_icon-placeholder">{chapter.id}</div>
                    )}
                  </div>
                  <h3 className="step1_chapter-title">{chapter.title}</h3>

                  {/* Progress bar */}
                  <div className="step1_chapter-progress">
                    <div className="step1_chapter-progress-bar">
                      <div
                        className="step1_chapter-progress-fill"
                        style={{
                          width: `${(() => {
                            // # Get chapter slug
                            let chapterSlug;
                            if (chapter.title === "Hematology & Oncology") {
                              chapterSlug = "hematology_and_oncology";
                            } else if (
                              chapter.title ===
                              "Musculoskeletal & Connective Tissue"
                            ) {
                              chapterSlug =
                                "musculoskeletal_and_connective_tissue";
                            } else {
                              chapterSlug = chapter.title
                                .toLowerCase()
                                .replace(/[\s&]+/g, "_");
                            }

                            // # Get chapter data and total possible topics
                            const chData = chapterData[chapterSlug];
                            const totalPossibleTopics =
                              chapterTopics[chapterSlug]?.length || 0;

                            if (!chData || totalPossibleTopics === 0) return 0;

                            // # If chapter is marked complete, return 100%
                            if (chData.iscomplete) return 100;

                            // # Count completed topics
                            const topicKeys = Object.keys(chData).filter(
                              (key) =>
                                key !== "iscomplete" &&
                                key !== "iscomplete_at" &&
                                key !== "IsChapStarted" &&
                                key !== "ChptStartedAt",
                            );

                            const completedCount = topicKeys.filter(
                              (key) => chData[key]?.completed,
                            ).length;

                            return Math.round(
                              (completedCount / totalPossibleTopics) * 100,
                            );
                          })()}%`,
                        }}
                      />
                    </div>
                    <span className="step1_chapter-progress-text">
                      {(() => {
                        // # Get chapter slug
                        let chapterSlug;
                        if (chapter.title === "Hematology & Oncology") {
                          chapterSlug = "hematology_and_oncology";
                        } else if (
                          chapter.title ===
                          "Musculoskeletal & Connective Tissue"
                        ) {
                          chapterSlug = "musculoskeletal_and_connective_tissue";
                        } else {
                          chapterSlug = chapter.title
                            .toLowerCase()
                            .replace(/[\s&]+/g, "_");
                        }

                        // # Get chapter data and total possible topics
                        const chData = chapterData[chapterSlug];
                        const totalPossibleTopics =
                          chapterTopics[chapterSlug]?.length || 0;

                        if (!chData || totalPossibleTopics === 0) return "0%";

                        // # If chapter is marked complete, return 100%
                        if (chData.iscomplete) return "100%";

                        // # Count completed topics
                        const topicKeys = Object.keys(chData).filter(
                          (key) =>
                            key !== "iscomplete" &&
                            key !== "iscomplete_at" &&
                            key !== "IsChapStarted" &&
                            key !== "ChptStartedAt",
                        );

                        const completedCount = topicKeys.filter(
                          (key) => chData[key]?.completed,
                        ).length;

                        return `${Math.round(
                          (completedCount / totalPossibleTopics) * 100,
                        )}%`;
                      })()}
                    </span>
                  </div>

                  {/* Add verification icon for completed chapters */}
                  {chapterData && chapterData[chapterSlug]?.iscomplete && (
                    <div className="step1_chapter-completed">
                      <FaCheckCircle className="step1_chapter-completed-icon" />
                    </div>
                  )}
                </div>
              );
            })}
        </div>
      </div>

      {/* Footer */}
      <DashboardFooter isDarkMode={isDarkMode} />
    </div>
  );
}

export default LibraryStep1;
